import { Box } from '@modusign/design-system';
import noop from 'lodash/noop';
import { ChangeEvent, CSSProperties } from 'react';

import TransformTextStyle from '@app/components/TextField/TransformTextStyle';
import iOS from '@app/libs/isiOS';
import { isSafari } from '@app/libs/isSafari';

interface DropdownFieldSelectProps {
  children: React.ReactChild[];
  transformTextStyle: Partial<TransformTextStyle>;
  defaultOptionLabel?: string;
  placeholder: string;
  tabIndex?: number;
  value?: string;
  style?: CSSProperties;
  buttonStyle?: CSSProperties;
  buttonHoverStyle?: CSSProperties;
  selectRef?: React.RefObject<HTMLSelectElement>;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  onHovered?: (hovered: boolean) => void;
}

export function DropdownFieldSelect({
  transformTextStyle,
  defaultOptionLabel,
  children,
  placeholder,
  value,
  tabIndex,
  style = {},
  buttonStyle = {},
  buttonHoverStyle = {},
  selectRef,
  onHovered = noop,
  onChange = noop,
}: DropdownFieldSelectProps) {
  return (
    <Box
      height="100%"
      overflow="visible"
      position="absolute"
      style={transformTextStyle}
      width="100%"
    >
      <Box
        sx={{
          lineHeight: 1,
          position: 'absolute',
          whiteSpace: 'nowrap',
          wordBreak: 'break-all',
          verticalAlign: 'top',
          '&::before': {
            content: '""',
            position: 'absolute',
            textAlign: 'center',
            zIndex: 1,
            right: '0.01em',
            top: '0.1em',
            bottom: '0.1em',
            width: '0.9em',
            opacity: '38%',
            height: '0.9em',
            borderRadius: '2px',
            backgroundColor: 'transparent',
            pointerEvents: 'none',
            ...buttonStyle,
          },
          '&:hover::before': buttonHoverStyle,
          '&::after': {
            content: '"\f0d7"',
            fontFamily: 'FontAwesome',
            position: 'absolute',
            textAlign: 'center',
            zIndex: 1,
            right: '0.05em',
            top: '0.1em',
            bottom: '0.1em',
            width: '0.8em',
            height: '0.8em',
            backgroundColor: 'transparent',
            pointerEvents: 'none',
          },
        }}
        width="100%"
      >
        <select
          ref={selectRef}
          style={{
            borderWidth: 0,
            // Select box에 의한 text 위치가 div와 달라서 경험적으로 구했습니다.
            marginTop: '-11px',
            textAlign: transformTextStyle.textAlign,
            fontFamily: transformTextStyle.fontFamily,
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
            paddingRight: '1em',
            outline: 'none',
            ...style,
          }}
          tabIndex={tabIndex}
          value={value}
          onChange={(e) => {
            onChange(e);
          }}
          onMouseEnter={() => onHovered?.(true)}
          onMouseLeave={() => onHovered?.(false)}
        >
          <option
            selected
            style={
              isSafari() || iOS
                ? undefined
                : {
                    display: 'none',
                  }
            }
            value=""
          >
            {placeholder}
          </option>
          {!(isSafari() || iOS) && <option style={{ color: 'black' }}>{defaultOptionLabel}</option>}
          {children}
        </select>
      </Box>
    </Box>
  );
}
